import { gql } from "@apollo/client";
import { QUERY_PAGE_SIZE } from "../util";
import { IPagedFirmaQuery } from "../../interfaces/firma";
import { getFields, getOrderByWithSecondOrder } from "../../util/graphqlUtil";
import { FirmaDtoFilterInput } from "../../interfaces/schema";
import { f } from "../../util/f";
import {
  firmaAbonnementFields,
  firmaAutoCompleteFields,
  firmaBeliggenhedsadresseFields,
  firmaBetalingsadresseFields,
  firmaDtoFields,
  firmaHistorik2Fields,
  firmaHistorikFields,
  firmaKlientErklaeringFields,
  firmaKorrespondanceFields,
  firmaPersonFirma,
  firmaSanktionFields,
  firmaTilsynFields,
  firmaejerskabFields,
} from "./fields";

export const GET_FIRMA_AUTOCOMPLETE_QUERY = ({ q }: { q: string }) => {
  const queryValues = q.split(" ").filter((value) => Boolean(value));

  const where = queryValues.length
    ? f<FirmaDtoFilterInput>()
        .addFilter(
          "and",
          queryValues.map((query) => {
            const queryNumber = Number(query);
            const isQueryNumber = !isNaN(queryNumber);

            if (isQueryNumber) {
              return {
                or: [
                  {
                    cvrNr: {
                      eq: queryNumber,
                    },
                  },
                  {
                    firmaNr: {
                      eq: queryNumber,
                    },
                  },
                ],
              };
            } else {
              return {
                navn1: {
                  contains: query,
                },
              };
            }
          }),
        )
        .build()
    : {};

  return gql`
    {
      firmaer(
        first: 5,
        order: { navn1: ASC },
        where: ${where}
        ) {
        edges {
          node {
            ${firmaAutoCompleteFields}
          }
        }
      }
    }
  `;
};

export const GET_PAGED_FIRMA_QUERY = (query: IPagedFirmaQuery) => {
  const { after, before, filters, fields } = query;
  const beforeStr = before
    ? "last: " + QUERY_PAGE_SIZE
    : "first: " + QUERY_PAGE_SIZE;
  const afterStr = after
    ? `after: "${after}"`
    : before
      ? `before: "${before}"`
      : "";

  const nodeFields = getFields(fields);
  const order = getOrderByWithSecondOrder(query, "firmaNr", "ASC");

  return gql`
    query pagedFirmaer {
      firmaer(
        ${order}
        ${beforeStr}
        ${afterStr}
        ${filters}
        ) {
          totalCount
          pageInfo {
            startCursor
            endCursor
            hasNextPage
            hasPreviousPage
          }
          edges {
            node {
              ${nodeFields}
            }
          }
        }
      }
  `;
};

export const GET_FIRMA_IDS_AND_EMAIL = (
  query: IPagedFirmaQuery,
  totalCount: number,
) => {
  const { filters } = query;

  return gql`
    query firmaIds {
      firmaer(
        ${filters ? filters : ""}
        first: ${totalCount}
      ) {
        totalCount
        edges {
          node {
            id
            email
          }
        }
      }
    }
  `;
};

export const GET_FIRMA_BY_ID_PERSONRELATIONER = () => gql`
  query firmaPersonrelationer($id: UUID!) {
    firma(id: $id) {
      id
      navn1
      firmaNr
      personFirma {
        id
        opretDato
        opretInit
        retteDato
        retteInit
        emailArbejde
        periode {
          fraDato
          tilDatoInklusiv
        }
        person {
          id
          kundeNr
          fornavne
          efternavn
          personart
          personPersonart {
            periode {
              fraDato
              tilDatoInklusiv
            }
            personartNr
            personartNrNavigation {
              nr
              navn
            }
          }
        }
        personfirmatypeNrNavigation {
          navn
        }
      }
    }
  }
`;
export const GET_FIRMAER_BY_IDS_INCLUDE_PERSONRELATIONER = (ids: string[]) => {
  const params = ids.map((id) => `{ id: { eq: "${id}" } }`).join(", ");
  return gql`
  {
    firmaer(
      where: { or: [${params}] }
    ) {
      edges {
        node {
          id
          navn1
          personFirma {
            id
            periode {
              fraDato
              tilDatoInklusiv
            }
            person {
              id
            }
            personfirmatypeNrNavigation {
              navn
            }
          }
          __typename
        }
        __typename
      }
      __typename
    }
  }
`;
};

export const GET_FIRMA_BY_ID_BETALINGSADRESSE = () => gql`
  query firmaBetalingsadresseQuery($id: UUID!) {
    firma(id: $id) {
    firmaNr
      firmaBetalingsadresse {
        ${firmaBetalingsadresseFields}
      }
    }
  }
`;

export const GET_FIRMA_STAMDATA_BY_ID_QUERY = () => gql`
query GET_FIRMA_STAMDATA_BY_ID_QUERY($id: UUID!) {
  firma(id: $id) {
    firmaNr
    firmahistorikNavigation {
      ${firmaHistorikFields}
    }
  }
}
`;
export const GET_FIRMA_STAMDATA_2_BY_ID_QUERY = () => gql`
query GET_FIRMA_STAMDATA_2_BY_ID_QUERY($id: UUID!) {
  firma(id: $id) {
    firmaNr
    firmahistorik2Navigation {
      ${firmaHistorik2Fields}
    }
  }
}
`;

export const GET_FIRMA_BY_ID_QUERY = () => {
  return gql`
        query firmaById($id: UUID!) {
          firma(id: $id) {
            ${firmaDtoFields}
            firmaBetalingsadresse {
              ${firmaBetalingsadresseFields}
            }
            beliggenhedsadresse {
              ${firmaBeliggenhedsadresseFields}
            }
            firmaAbonnement {
              ${firmaAbonnementFields}
            }
            firmaejerskabEjendeFirmaNavigation {
              ${firmaejerskabFields}
            }
            firmaejerskabEjetFirma {
              ${firmaejerskabFields}
            }
            firmahistorikNavigation {
              ${firmaHistorikFields}
            }
            firmahistorik2Navigation {
              ${firmaHistorik2Fields}
            }
            firmaKorrespondance {
              ${firmaKorrespondanceFields}
            }
            personFirma {
              ${firmaPersonFirma}
            }
          }
        }
      `;
};
export const GET_FIRMA_TILSYN_BY_ID_QUERY = () => {
  return gql`
        query firmaTilsynById($id: UUID!) {
          firma(id: $id) {
            firmaTilsyn {
              ${firmaTilsynFields}
            }
            firmaKlienterklaering {
              ${firmaKlientErklaeringFields}
            }
        
          }
        }
      `;
};
export const GET_FIRMA_DISCIPLINAERT_BY_ID_QUERY = () => {
  return gql`
        query firmaDisciplinaertById($id: UUID!) {
          firma(id: $id) {
            firmaSanktion {
              ${firmaSanktionFields}
            }
          }
        }
      `;
};

export const FIRMA_BY_ID_TICKETDATA = gql`
  query firmaById_ticketdata($id: UUID!) {
    firma(id: $id) {
      id
      navn1
      navn2
      firmaNr
      kommuneNr
    }
  }
`;

export const GET_FIRMA_BY_PNR_QUERY = (query: { pnr: number }) => {
  const { pnr } = query;
  const clause = `pnr: { eq: ${pnr} } `;
  return gql`
    query firmaByPnr{
      firmaer(
      first: 1,
      where: {
      ${clause}
      }) {
        edges {
          node {
            id 
            navn1
            cvrNr
            pnr
          }
        }
      }
    }
  `;
};
